/*
  <marquee-band data-speed="60" data-direction="left|right" [data-pause-hover] [data-scroll-reactive]>
  Drives the track with requestAnimationFrame so speed can ease (hover pause)
  and respond to page scroll velocity. Without this script, a CSS keyframe loop runs instead.
*/
(() => {
  if (customElements.get('marquee-band')) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* One shared scroll tracker for every marquee on the page. */
  const scrollState = { velocity: 0, direction: 1, lastY: window.scrollY, lastT: performance.now() };
  window.addEventListener('scroll', () => {
    const now = performance.now();
    const dy = window.scrollY - scrollState.lastY;
    const dt = Math.max(16, now - scrollState.lastT);
    scrollState.velocity = dy / dt; // px per ms
    if (dy !== 0) scrollState.direction = dy > 0 ? 1 : -1;
    scrollState.lastY = window.scrollY;
    scrollState.lastT = now;
  }, { passive: true });

  class MarqueeBand extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      this.group = this.querySelector('[data-marquee-group]');
      if (!this.track || !this.group || reduceMotion()) return;

      this.baseSpeed = parseFloat(this.dataset.speed) || 60;
      this.baseDir = this.dataset.direction === 'right' ? 1 : -1;
      this.reactive = this.hasAttribute('data-scroll-reactive');
      this.offset = 0;
      this.ease = 1; // 0 = paused, 1 = full speed
      this.easeTarget = 1;
      this.boost = 0;
      this.flow = 1; // follows scroll direction when reactive
      this.running = false;
      this.last = 0;

      this.classList.add('is-driven');
      this.fill();

      if (this.hasAttribute('data-pause-hover')) {
        const pause = () => { this.easeTarget = 0; };
        const resume = () => { this.easeTarget = 1; };
        this.addEventListener('pointerenter', pause);
        this.addEventListener('pointerleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
      }

      this.resizeObserver = new ResizeObserver(() => this.fill());
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.group); // images loading late change the loop width

      this.visibility = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) this.start();
        else this.stop();
      });
      this.visibility.observe(this);
    }

    disconnectedCallback() {
      this.stop();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.visibility) this.visibility.disconnect();
    }

    /* Make sure the track is at least two viewport-widths long so the loop never shows a gap. */
    fill() {
      const groupWidth = this.group.getBoundingClientRect().width;
      if (!groupWidth) return;
      this.groupWidth = groupWidth;
      const needed = Math.max(2, Math.ceil(this.clientWidth / groupWidth) + 1);
      const groups = this.track.children;
      while (groups.length < needed) {
        const copy = this.group.cloneNode(true);
        copy.removeAttribute('data-marquee-group');
        copy.setAttribute('aria-hidden', 'true');
        copy.setAttribute('inert', '');
        copy.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
        this.track.appendChild(copy);
      }
    }

    start() {
      if (this.running) return;
      this.running = true;
      this.last = performance.now();
      const step = (now) => {
        if (!this.running) return;
        this.tick(Math.min(64, now - this.last) / 1000);
        this.last = now;
        this.raf = requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);
    }

    stop() {
      this.running = false;
      if (this.raf) cancelAnimationFrame(this.raf);
    }

    tick(dt) {
      if (!this.groupWidth) return;
      this.ease += (this.easeTarget - this.ease) * Math.min(1, dt * 6);

      if (this.reactive) {
        // Turn scroll velocity into a temporary speed boost that decays smoothly.
        const target = Math.min(6, Math.abs(scrollState.velocity) * 4);
        this.boost += (target - this.boost) * Math.min(1, dt * 8);
        scrollState.velocity *= Math.pow(0.02, dt);
        this.flow += (scrollState.direction - this.flow) * Math.min(1, dt * 4);
      }

      const speed = this.baseSpeed * this.ease * (1 + this.boost);
      this.offset += this.baseDir * this.flow * speed * dt;

      // Wrap within one group width (the groups are identical copies).
      const w = this.groupWidth;
      if (this.offset <= -w) this.offset += w;
      if (this.offset > 0) this.offset -= w;
      this.track.style.transform = `translate3d(${this.offset.toFixed(2)}px, 0, 0)`;
    }
  }

  customElements.define('marquee-band', MarqueeBand);
})();
